import { create } from "zustand";
import marketdata from "../../data/Services/marketdata";
import { settings } from "../../data/Services/settings.jsx";
import { eventBus, EVENTS } from "../../utils/eventBus";
import {
  getAutoSavedWorkspace,
  loadAutoSavedWorkspace,
  clearAutoSavedWorkspace,
} from "../../workspace";
import { restoreSelectedDesktopOnBoot } from "../../workspace/desktopManager";
import logger from "../../utils/logger";
import { globalToast } from "../../components/ToastProvider.jsx";

// Import state and configurations
import { initialChartState } from "./chartState";

// Import data processing functions
import { fetchChartData, handleQuoteData } from "./chartDataProcessor";

// Import action modules
import { chartActions } from "./chartActions";
import { chartSelectors } from "./chartSelectors";
import { interactiveActions } from "./chartInteractiveActions";
import { sectionActions } from "./chartSectionActions";

// Module-level debounce for gap reload (avoids Zustand re-renders)
const _lastGapReload = new Map(); // symbol → timestamp

export const useChartsStore = create((set, get) => ({
  ...initialChartState,

  // Initialize store
  initialize: async () => {
    if (get()._initialized) return; // Prevent multiple initializations
    set({ _initialized: true });

    const { onQuote, addNewChart, reloadAllChartsData, loadWorkspace } = get();
    marketdata.registerReceiver({ OnQuote: onQuote });

    // Check for auto-saved workspace and offer to restore it
    try {
      // Get user/broker context from marketdata (available after authentication)
      const user = marketdata.user;
      const broker = marketdata.broker;

      if (!user || !broker) {
        console.warn(
          "[ChartStore] User/broker not available yet, skipping auto-restore",
        );
      } else {
        // "Meus Desktops": reopen on the persisted selected desktop (local
        // cache first, server fallback). Falls through to the legacy
        // auto-save slot when no desktop id is tracked yet.
        const restoredSelected = await restoreSelectedDesktopOnBoot(
          () => get(),
          loadWorkspace,
        );
        if (restoredSelected) {
          set({ _wasAutoRestored: true });
          return;
        }

        const autoSavedData = getAutoSavedWorkspace(user, broker);

        if (autoSavedData) {
          const autoSaveDate = new Date(autoSavedData.timestamp);
          console.log(
            `Auto-restoring workspace from ${autoSaveDate.toLocaleString()}`,
          );

          const success = loadAutoSavedWorkspace(loadWorkspace, user, broker);

          if (success) {
            console.log(
              `✅ Workspace automatically restored from ${autoSaveDate.toLocaleString()}`,
            );
            set({ _wasAutoRestored: true });

            // Clear the auto-saved data since we've successfully restored it
            clearAutoSavedWorkspace(user, broker);
            return; // Skip adding new chart since we loaded the workspace
          } else {
            console.warn(
              "Failed to auto-restore workspace, falling back to new chart",
            );
          }
        } else {
          console.log("No auto-saved workspace found");
        }
      }
    } catch (error) {
      console.error("Error during auto-restore check:", error);
      logger.workspace.warning("Erro ao verificar workspace auto-salvo", {
        error: error.message,
      });
    }

    // If no auto-restore happened, create a new chart as usual
    await addNewChart();

    // Set up event listeners to break circular dependencies
    eventBus.on(EVENTS.CHART_DATA_RELOAD_REQUESTED, reloadAllChartsData);
  },

  // Handle real-time quotes
  // PERFORMANCE: Updates chartData map instead of charts array
  // This prevents Workspace re-renders on every quote update
  onQuote: (quote) => {
    // Ignore snapshot quotes (TY="H") - charts don't need them
    if (quote.TY === "H") return;

    // Auto-reload chart data when trade gap detected (debounced 5s per symbol)
    // Feature flag: runtime-config.json (production) or VITE env var (dev)
    if (
      (settings.gapAutoReload ||
        import.meta.env.VITE_GAP_AUTO_RELOAD === "true") &&
      quote._gapDetected
    ) {
      const now = Date.now();
      const lastReload = _lastGapReload.get(quote.S) || 0;
      if (now - lastReload > 5000) {
        _lastGapReload.set(quote.S, now);
        const charts = get().charts;
        for (const chart of charts) {
          const symbolMatches =
            chart.symbol === quote.S ||
            marketdata.isSameFamily(chart.symbol, quote.S);
          if (symbolMatches) {
            console.log(
              `[ChartStore] 🔄 Trade gap for ${quote.S}, reloading chart ${chart.id}`,
            );
            get().fetchChartData(chart.id); // fire-and-forget
          }
        }
      }
      // Don't return — still process the quote normally below
    }

    // Block real quotes during replay mode (only allow replay quotes)
    if (
      typeof window !== "undefined" &&
      window.__REPLAY_MODE_ACTIVE__ === true
    ) {
      // Only allow quotes marked as replay quotes
      if (!quote._isReplay) {
        console.warn(
          "[ChartStore] Real-time quote blocked during replay mode:",
          quote.S,
        );
        return;
      }
    }

    set((state) => {
      // Normally only chartData changes (Workspace won't re-render). `charts`
      // comes back === state.charts unless a parked-left viewport had to follow
      // a new bar (issue #163), in which case it's a new ref and the Workspace
      // re-renders once for that bar.
      const { chartData, charts } = handleQuoteData(
        quote,
        state.charts,
        state.chartData,
      );
      return { chartData, charts };
    });
  },

  // Reload all charts data (called via event bus)
  reloadAllChartsData: async () => {
    try {
      console.log(
        `[${new Date().toISOString()}] 🔄 Reloading historical data for all charts after reconnection...`,
      );

      // Get all charts and reload their data
      const charts = get().charts;
      console.log(
        `[${new Date().toISOString()}] 📊 Found ${charts.length} charts to reload`,
      );

      const reloadPromises = charts.map(async (chart) => {
        try {
          console.log(
            `[${new Date().toISOString()}] 📈 Reloading data for chart ${chart.id} (${chart.symbol})`,
          );
          await get().fetchChartData(chart.id);
          console.log(
            `[${new Date().toISOString()}] ✅ Successfully reloaded data for chart ${chart.id}`,
          );
        } catch (error) {
          console.error(
            `[${new Date().toISOString()}] ❌ Failed to reload data for chart ${chart.id}:`,
            error,
          );
        }
      });

      await Promise.all(reloadPromises);
      console.log(
        `[${new Date().toISOString()}] ✅ All chart data reloaded successfully`,
      );
    } catch (error) {
      console.error(
        `[${new Date().toISOString()}] ❌ Error reloading chart data:`,
        error,
      );
    }
  },

  // Fetch chart data
  fetchChartData: async (chartId) => {
    const chart = get().getChart(chartId);
    if (!chart) return;

    await fetchChartData(chart, get().updateChartConfig, get().setData);
    // Nudge: force a dataVersion increment to ensure rendering pipeline
    // picks up the fresh data, even without real-time quotes flowing
    get().nudgeChart(chartId);
  },

  // Combine all actions
  ...chartActions(set, get),
  ...chartSelectors(get),
  ...interactiveActions(set),
  ...sectionActions(set, get),
}));

// Expose store on window for error reporting
if (typeof window !== "undefined") {
  window.__CHARTS_STORE__ = useChartsStore.getState();
  // Update reference when store changes (for error reporting)
  useChartsStore.subscribe((state) => {
    window.__CHARTS_STORE__ = state;
  });
}
